
import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import { Settings, CheckCircle2, ChevronDown, Image as ImageIcon, Trash2, X, Shuffle, ArrowUpToLine, LayoutGrid } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { marked } from 'marked';
import TurndownService from 'turndown';
import ImageEditor from './ImageEditor';
import { addWatermark } from '../lib/watermark';
import { GenerationTask, UploadedImage } from '../types';
import { generateFullHtml } from '../lib/htmlTemplate';
import { prepareMarkdownForRender } from '../lib/markdown';
import { splitLongParagraphs } from '../lib/splitParagraphs';
import { getDefaultCategory } from '../lib/category';
import { ImageUploader } from './ImageUploader';

interface EditorProps {
  initialContent: string;
  articleTitle?: string;
  onBack: () => void;
  onSave: (content: string, articleTitle: string) => Promise<void>;
  onImagesChange?: (taskId: string, images: string[]) => Promise<void>;
  tasks?: GenerationTask[];
  agents?: { id: string; name: string }[];
  activeTaskId?: string;
  onSwitchTask?: (taskId: string) => void;
  isRegistered?: boolean;
  isWeekend?: boolean;
  category?: string;
  onToggleRegister?: (isRegistered: boolean) => void;
  onToggleWeekend?: (isWeekend: boolean) => void;
  onToggleCategory?: (category: string) => void;
  onRenameTask?: (taskId: string, newName: string) => Promise<void>;
  selectedAgentName: string | 'all';
  onSelectAgent: (agentName: string | 'all') => void;
  isSidebarOpen?: boolean;
  categories: string[];
  onAddCategory: () => void;
  onRemoveCategory: (cat: string) => void;
  newCategoryName: string;
  setNewCategoryName: (val: string) => void;
  showAddCategory: boolean;
  setShowAddCategory: (val: boolean) => void;
}

const applyUserCleaningRules = (content: string) => {
  let cleaned = content;
  
  // 1. 이미지 파일 바로 다음 한줄의 문구가 이미지 알트 텍스트와 정확히 동일할 경우만 삭제
  //    URL 납부에 괄호가 있어도 깨지지 않도록 균형 잡힌 괄호 매칭 사용.
  cleaned = cleaned.replace(/(!\[(.*?)\]\((?:[^()]|\([^)]*\))*\))[ \t]*\r?\n[ \t]*([^\r\n]+)/g, (match, p1, p2, p3) => {
    const altText = p2.trim();
    const nextLine = p3.trim();
    if (altText && nextLine && altText === nextLine) {
      return p1;
    }
    return match;
  });

  // 2. ▶, ▮, ◆ 앞의 숫자와 마침표 제거 (예: "1. ▶ " -> "▶ ")
  // 소제목 패턴: 숫자 + 마침표 + 공백(옵션) + 특수기호(들)
  cleaned = cleaned.replace(/^(\s*\d+\.\s*)([▶▮◆]+)/gm, '$2');

  // 3. 문장이 3개를 초과하는 문단을 2~3개 문장 단위로 분할
  cleaned = splitLongParagraphs(cleaned);

  return cleaned;
};

const isCaptionNode = (node: Node | null): boolean => {
  if (!node) return false;
  const text = node.textContent?.trim() || "";
  // Check for common caption starters
  return text.startsWith("출처") || text.startsWith("[출처") || text.startsWith("(출처") || text.startsWith("ⓒ") || text.startsWith("Copyright") || text.startsWith("©");
};

const findCaptionNode = (imgNode: HTMLImageElement, editor: HTMLElement): (Element | Text) | undefined => {
  if (!imgNode || !editor) return undefined;

  // 1. Check immediate next sibling (could be a tag or a text node)
  let next = imgNode.nextSibling;
  
  // Skip small whitespace text nodes or BRs
  while (next && (
    (next.nodeType === Node.TEXT_NODE && next.textContent?.trim().length === 0) || 
    (next.nodeType === Node.ELEMENT_NODE && (next as Element).tagName === 'BR')
  )) {
    next = next.nextSibling;
  }

  if (next && isCaptionNode(next)) return next as (Element | Text);

  // 2. If image is inside a block (like <p>), check the block's next sibling
  const parent = imgNode.parentElement;
  if (parent && parent !== editor) {
    // Check if there is any more content in the parent after this image
    let nextSib = imgNode.nextSibling;
    let hasSubsequentContent = false;
    while (nextSib) {
        if (nextSib.textContent?.trim().length) {
            hasSubsequentContent = true;
            break;
        }
        nextSib = nextSib.nextSibling;
    }

    if (!hasSubsequentContent) {
        let nextBlock = parent.nextElementSibling;
        if (nextBlock && isCaptionNode(nextBlock)) return nextBlock as Element;
    }
  }
  
  return undefined;
};

const WATERMARK_AGENTS = ['카텍 퍼플', '카텍 lm', '노머니 lm', 'life lm', '연예 lm'];

// 에디터 본문의 출처 캡션 표시/숨김. Text 노드 캡션은 span.source-caption으로 감싸 스타일 적용.
const setSourceCaptionsVisibility = (editor: HTMLElement, visible: boolean) => {
  editor.querySelectorAll('img').forEach(img => {
    let cap = findCaptionNode(img as HTMLImageElement, editor);
    if (!cap) return;
    if (cap.nodeType === Node.TEXT_NODE) {
      const span = document.createElement('span');
      span.className = 'source-caption';
      cap.parentNode?.insertBefore(span, cap);
      span.appendChild(cap);
      cap = span;
    } else {
      (cap as Element).classList.add('source-caption');
    }
    (cap as HTMLElement).style.display = visible ? '' : 'none';
  });
};

export const Editor: React.FC<EditorProps> = ({ 
  initialContent, 
  articleTitle: initialArticleTitle,
  onBack, 
  onSave,
  onImagesChange,
  tasks = [],
  agents = [],
  activeTaskId,
  onSwitchTask,
  isRegistered = false,
  isWeekend = false,
  category = '',
  onToggleRegister,
  onToggleWeekend,
  onToggleCategory,
  onRenameTask,
  selectedAgentName,
  onSelectAgent,
  isSidebarOpen = true,
  categories,
  onAddCategory,
  onRemoveCategory,
  newCategoryName,
  setNewCategoryName,
  showAddCategory,
  setShowAddCategory
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const recTitlesEditorRef = useRef<HTMLDivElement>(null);
  const lastSavedContentRef = useRef<string>(initialContent);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [title, setTitle] = useState('');
  const [recommendedTitles, setRecommendedTitles] = useState('');
  const [lastSaveStatus, setLastSaveStatus] = useState<'saved' | 'saving' | 'idle'>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [selectedImgNodes, setSelectedImgNodes] = useState<HTMLImageElement[]>([]);
  const [isWatermarking, setIsWatermarking] = useState(false);
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((message: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, visible: true });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 2000);
  }, []);

  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [tempPresetName, setTempPresetName] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const activeTask = useMemo(() => tasks.find(t => t.id === activeTaskId), [tasks, activeTaskId]);
  const effectiveCategory = category || (activeTask ? getDefaultCategory(activeTask.presetName) : '');

  const getFullMarkdown = useCallback(() => {
    if (!editorRef.current) return '';
    const turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced'
    });
    const bodyContent = turndownService.turndown(editorRef.current.innerHTML);
    
    // Structure: Recommended Titles \n\n ~~~ \n\n Body \n\n ---
    return `${recommendedTitles.trim()}\n\n~~~\n\n${bodyContent.trim()}\n\n---`;
  }, [recommendedTitles]);

  const saveContent = useCallback(async (isAutoSave = false) => {
    if (!editorRef.current) return;
    
    const fullMarkdown = getFullMarkdown();
    const currentTitle = title.trim();
    
    // CRITICAL: Only save if content actually changed AND isDirty is true
    // This prevents "orange title" bug when just clicking/loading
    if (!isDirty && fullMarkdown.trim() === lastSavedContentRef.current.trim() && currentTitle === title.trim()) {
        return;
    }

    lastSavedContentRef.current = fullMarkdown;
    setIsDirty(false);
    
    if (isAutoSave) {
        setLastSaveStatus('saving');
        setTimeout(() => {
            setLastSaveStatus('saved');
            setTimeout(() => setLastSaveStatus('idle'), 2000);
        }, 1000);
    }
    
    await onSave(fullMarkdown, currentTitle);
  }, [getFullMarkdown, onSave, title, isDirty]);

  const saveContentRef = useRef(saveContent);
  useEffect(() => {
    saveContentRef.current = saveContent;
  }, [saveContent]);

  const historyRef = useRef<{ snapshots: string[]; index: number }>({ snapshots: [], index: -1 });
  const HISTORY_LIMIT = 100;

  const pushHistory = useCallback(() => {
    if (!editorRef.current) return;
    const current = editorRef.current.innerHTML;
    const h = historyRef.current;
    if (h.snapshots[h.index] === current) return;
    h.snapshots = h.snapshots.slice(0, h.index + 1);
    h.snapshots.push(current);
    h.index = h.snapshots.length - 1;
    if (h.snapshots.length > HISTORY_LIMIT) {
      h.snapshots.shift();
      h.index -= 1;
    }
  }, []);

  const handleUndo = useCallback(() => {
    const h = historyRef.current;
    if (h.index <= 0 || !editorRef.current) return;
    h.index -= 1;
    editorRef.current.innerHTML = h.snapshots[h.index];
    setIsDirty(true);
    saveContentRef.current(true);
  }, []);

  const handleRedo = useCallback(() => {
    const h = historyRef.current;
    if (h.index >= h.snapshots.length - 1 || !editorRef.current) return;
    h.index += 1;
    editorRef.current.innerHTML = h.snapshots[h.index];
    setIsDirty(true);
    saveContentRef.current(true);
  }, []);

  const debouncedSave = useCallback(() => {
    setIsDirty(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      pushHistory();
      saveContentRef.current(true);
    }, 2000); // 2 second debounce for real-time auto-save
  }, [pushHistory]);

  const isWatermarkTarget = useMemo(() => {
    if (!activeTask?.agentName) return false;
    const name = activeTask.agentName.toLowerCase();
    return WATERMARK_AGENTS.some(target => name.includes(target.toLowerCase()));
  }, [activeTask?.agentName]);

  const applyWatermarksToEditor = useCallback(async (forceAll = false) => {
    const editor = editorRef.current;
    if (!editor) return;
    
    // Automatic processing only for target agents
    if (!forceAll && !isWatermarkTarget) return;
    
    // Find images.
    const selector = 'img';
    const imgs = Array.from(editor.querySelectorAll(selector)) as HTMLImageElement[];
    if (imgs.length === 0) {
      if (forceAll) showToast('편집기에 이미지가 없습니다.');
      return;
    }

    let processedCount = 0;
    let failCount = 0;
    
    for (const img of imgs) {
      // Skip if already watermarked with data URI
      if (img.src.startsWith('data:image/') && img.getAttribute('data-watermarked') === 'true') {
        continue;
      }

      // If forceAll is false (Auto-run), we skip already marked ones
      if (!forceAll && img.getAttribute('data-watermarked') === 'true') continue;

      const processImg = async () => {
        try {
          if (!img.complete || img.naturalWidth === 0) {
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => reject(new Error('Timeout')), 20000);
              img.onload = () => { clearTimeout(timeout); resolve(true); };
              img.onerror = () => { clearTimeout(timeout); reject(new Error('Load failed')); };
            });
          }
          
          const watermarkedBase64 = await addWatermark(img.src);
          if (watermarkedBase64 && watermarkedBase64.startsWith('data:image/')) {
            img.src = watermarkedBase64;
            img.setAttribute('data-watermarked', 'true');
            processedCount++;
          } else {
            failCount++;
          }
        } catch (e) {
          console.error('Watermark failed for image:', img.src, e);
          failCount++;
        }
      };

      await processImg();
    }
    
    if (processedCount > 0) {
      setIsDirty(true);
      pushHistory();
      debouncedSave();
    }

    if (forceAll) {
      if (processedCount === 0 && failCount === 0) {
        showToast('이미 워터마크가 모두 적용되어 있습니다.');
      } else {
        showToast(`워터마크 작업이 완료되었습니다. 성공: ${processedCount}개, 실패: ${failCount}개`);
      }
    }
  }, [isWatermarkTarget, debouncedSave, pushHistory]);

  const filteredTasks = useMemo(() => {
    let result = selectedAgentName === 'all' ? [...tasks] : tasks.filter(t => t.agentName === selectedAgentName);
    // Sort by createdAt descending to move recently modified tasks to the top
    return result.sort((a, b) => b.createdAt - a.createdAt);
  }, [tasks, selectedAgentName]);

  useEffect(() => {
    // Guard 1: If the new initialContent matches what we just saved, skip re-parsing to avoid UI glitches
    if (initialContent.trim() === lastSavedContentRef.current.trim() && editorRef.current?.innerHTML) {
      return;
    }

    // Guard 2: If the user has unsaved changes (isDirty), don't overwrite their edits from external changes
    if (isDirty && editorRef.current?.innerHTML) {
      return;
    }

    const cleanedContent = applyUserCleaningRules(initialContent);

    let initialTitle = initialArticleTitle || `Document-${new Date().toISOString().slice(0, 10)}`;
    let bodyMarkdown = cleanedContent;
    let recTitles = '';

    // Separate recommended titles and body based on ~~~ and ---
    const waveIndex = cleanedContent.indexOf('~~~');
    if (waveIndex !== -1) {
        recTitles = cleanedContent.substring(0, waveIndex).trim();
        const rest = cleanedContent.substring(waveIndex + 3);
        const dashIndex = rest.indexOf('---');
        if (dashIndex !== -1) {
            bodyMarkdown = rest.substring(0, dashIndex).trim();
            const afterDash = rest.substring(dashIndex + 3).trim();
            if (afterDash) {
              bodyMarkdown = bodyMarkdown + '\n\n---\n\n' + afterDash;
            }
        } else {
            bodyMarkdown = rest.trim();
        }
    } else {
        // Fallback to legacy title extraction if no ~~~ markers found
        const separatorRegex = /(?:^|\n)={3,}(?:\n|$)/;
        const separatorMatch = cleanedContent.match(separatorRegex);

        if (separatorMatch && separatorMatch.index !== undefined) {
          const extractedTitle = cleanedContent.substring(0, separatorMatch.index).trim();
          if (!initialArticleTitle) initialTitle = extractedTitle;
          bodyMarkdown = cleanedContent.substring(separatorMatch.index + separatorMatch[0].length).trim();
        } else {
          const h1Regex = /(?:^|\n)#\s+(.+)(?:\n|$)/;
          const h1Match = cleanedContent.match(h1Regex);

          if (h1Match) {
              const extractedTitle = h1Match[1].trim();
              if (!initialArticleTitle) initialTitle = extractedTitle;
              bodyMarkdown = cleanedContent.replace(h1Match[0], '').trim();
          } else {
              const lines = cleanedContent.split('\n').filter(l => l.trim().length > 0);
              if (lines.length > 0 && lines[0].length < 100) {
                  const extractedTitle = lines[0].replace(/^[#\s\*]+/, '').trim();
                  if (!initialArticleTitle) initialTitle = extractedTitle;
                  bodyMarkdown = cleanedContent.substring(cleanedContent.indexOf(lines[0]) + lines[0].length).trim();
              }
          }
        }
    }

    // Clean up internal metadata headers
    initialTitle = initialTitle.replace(/^##\s*\[.*?(?:제목\s*후보|본문).*?\](?:.*)?$/gm, '').trim();
    bodyMarkdown = bodyMarkdown.replace(/^##\s*\[.*?(?:제목\s*후보|본문).*?\](?:.*)?$/gm, '').trim();
    recTitles = recTitles.replace(/^##\s*\[.*?(?:제목\s*후보|본문).*?\](?:.*)?$/gm, '').trim();

    if (!initialTitle) initialTitle = "Untitled Document";
    
    setTitle(initialTitle);
    setRecommendedTitles(recTitles);
    
    setSelectedImgNodes([]);
    setShowImageEditor(false);
    setEditingImage(null);
    setShowFindReplace(false);
    setLastSaveStatus('idle');

    lastSavedContentRef.current = bodyMarkdown;

    if (editorRef.current) {
      const parseMarkdown = async () => {
        try {
            const html = await marked.parse(prepareMarkdownForRender(bodyMarkdown));
            if (editorRef.current) {
                editorRef.current.innerHTML = html;
                // 출처 캡션은 기본적으로 숨김 (앨범의 "출처 표시" 체크박스로 표시 가능)
                setSourceCaptionsVisibility(editorRef.current, showSourceCaptions);
                historyRef.current = { snapshots: [editorRef.current.innerHTML], index: 0 };
            }
        } catch (e) {
            console.error("Markdown parsing failed", e);
            if (editorRef.current) {
                editorRef.current.innerText = bodyMarkdown;
                historyRef.current = { snapshots: [editorRef.current.innerHTML], index: 0 };
            }
        }
      };
      parseMarkdown();
    }

    if (recTitlesEditorRef.current) {
      const parseRecTitles = async () => {
        try {
            const html = await marked.parse(prepareMarkdownForRender(recTitles));
            if (recTitlesEditorRef.current) {
                recTitlesEditorRef.current.innerHTML = html;
            }
        } catch (e) {
            console.error("Recommended titles markdown parsing failed", e);
            if (recTitlesEditorRef.current) {
                recTitlesEditorRef.current.innerText = recTitles;
            }
        }
      };
      parseRecTitles();
    }
  }, [initialContent, applyWatermarksToEditor, initialArticleTitle]);

  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const scrollPosRef = useRef<number>(0);
  const isSwitchingRef = useRef(false);

  const handleSidebarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isSwitchingRef.current) return;
    scrollPosRef.current = e.currentTarget.scrollTop;
  };

  useLayoutEffect(() => {
    if (sidebarScrollRef.current) {
      sidebarScrollRef.current.scrollTop = scrollPosRef.current;
      
      // Force restoration after a frame to handle any delayed layout shifts
      const rafId = requestAnimationFrame(() => {
        if (sidebarScrollRef.current) {
          sidebarScrollRef.current.scrollTop = scrollPosRef.current;
        }
        // Release the lock after the browser has had a chance to settle
        setTimeout(() => {
          isSwitchingRef.current = false;
        }, 50);
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [activeTaskId, filteredTasks, selectedAgentName]);

  useEffect(() => {
    if (editingTaskId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingTaskId]);

  const handleStartRename = (e: React.MouseEvent, task: GenerationTask) => {
    e.stopPropagation();
    setEditingTaskId(task.id);
    setTempPresetName(task.presetName);
  };

  const handleFinishRename = async () => {
    if (editingTaskId && tempPresetName.trim()) {
      await onRenameTask?.(editingTaskId, tempPresetName.trim());
    }
    setEditingTaskId(null);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFinishRename();
    } else if (e.key === 'Escape') {
      setEditingTaskId(null);
    }
  };

  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImage, setEditingImage] = useState<UploadedImage | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');

  const [showEditMenu, setShowEditMenu] = useState(false);
  const [showAlbum, setShowAlbum] = useState(false);
  const [showSourceCaptions, setShowSourceCaptions] = useState(false);
  const [selectedAlbumIndices, setSelectedAlbumIndices] = useState<Set<number>>(new Set());
  const [selectionBox, setSelectionBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const albumGridRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleEditorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Handle Link clicks
      const anchor = target.closest('a');
      if (anchor && anchor.getAttribute('href')) {
        const href = anchor.getAttribute('href');
        if (href && (href.startsWith('http') || href.startsWith('mailto:'))) {
          e.preventDefault();
          e.stopPropagation();
          window.open(href, '_blank', 'noopener,noreferrer');
          return;
        }
      }

      if (target.tagName === 'IMG') {
        const img = target as HTMLImageElement;
        const isMultiSelect = e.shiftKey || e.ctrlKey || e.metaKey;

        if (isMultiSelect) {
          setSelectedImgNodes(prev => {
            if (prev.includes(img)) {
              img.classList.remove('selected-img');
              const caption = findCaptionNode(img, editor);
              if (caption instanceof Element) caption.classList.remove('selected-caption');
              return prev.filter(n => n !== img);
            } else {
              img.classList.add('selected-img');
              const caption = findCaptionNode(img, editor);
              if (caption instanceof Element) caption.classList.add('selected-caption');
              return [...prev, img];
            }
          });
        } else {
          editor.querySelectorAll('img').forEach(i => i.classList.remove('selected-img'));
          editor.querySelectorAll('.selected-caption').forEach(c => c.classList.remove('selected-caption'));
          
          img.classList.add('selected-img');
          const caption = findCaptionNode(img, editor);
          if (caption instanceof Element) caption.classList.add('selected-caption');
          
          setSelectedImgNodes([img]);
        }
        
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          const caption = findCaptionNode(img, editor);
          if (caption) {
            range.setStartBefore(img);
            range.setEndAfter(caption);
          } else {
            range.selectNode(img);
          }
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        editor.querySelectorAll('img').forEach(img => img.classList.remove('selected-img'));
        editor.querySelectorAll('.selected-caption').forEach(c => c.classList.remove('selected-caption'));
        setSelectedImgNodes([]);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedImgNodes.length > 0) {
          e.preventDefault();
          selectedImgNodes.forEach(node => {
            const caption = findCaptionNode(node, editor);
            if (caption) {
              caption.parentNode?.removeChild(caption);
            }
            node.remove();
          });
          setSelectedImgNodes([]);
          saveContent(true);
        }
      }
    };

    editor.addEventListener('click', handleEditorClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      editor.removeEventListener('click', handleEditorClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImgNodes, saveContent]);

  useEffect(() => {
    if (titleInputRef.current) {
        titleInputRef.current.style.height = 'auto';
        titleInputRef.current.style.height = titleInputRef.current.scrollHeight + 'px';
    }
  }, [title]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isDirty) saveContent(true);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [saveContent, isDirty]);

  const handleBack = async () => {
    if (isDirty) await saveContent(false);
    onBack();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      let val = e.target.value;
      if (val.length > 50) {
        val = val.slice(0, 50);
      }
      setTitle(val);
      debouncedSave();
  };

  const handleRecTitlesInput = () => {
      if (!recTitlesEditorRef.current) return;
      const turndownService = new TurndownService({
          headingStyle: 'atx',
          codeBlockStyle: 'fenced'
      });
      const md = turndownService.turndown(recTitlesEditorRef.current.innerHTML);
      setRecommendedTitles(md);
      debouncedSave();
  };

  const execCmd = async (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    pushHistory();

    // Ensure commands trigger a save
    await saveContent(true);
  };

  const distributeImages = () => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    
    // Identify captions and pair them with images
    const allImages = Array.from(editor.querySelectorAll('img'));
    
    // Find all pairs first without removing them yet
    const imageGroups: { img: HTMLImageElement, caption?: Node }[] = [];
    allImages.forEach(img => {
      const caption = findCaptionNode(img, editor);
      imageGroups.push({ img, caption });
    });

    if (imageGroups.length === 0) {
      showToast("배치할 이미지가 없습니다.");
      return;
    }

    // Now REMOVE them all from the DOM to leave only content blocks
    imageGroups.forEach(group => {
      if (group.caption) {
        group.caption.parentNode?.removeChild(group.caption);
      }
      group.img.parentNode?.removeChild(group.img);
    });

    // Get top-level block elements
    // We use childNodes to include text nodes if they are at the top level
    const allNodes = Array.from(editor.childNodes);
    const nonImageElements = allNodes.filter(node => node.textContent?.trim().length > 0);

    if (nonImageElements.length === 0) {
      showToast("이미지를 배치할 텍스트 문단이 없습니다.");
      return;
    }

    // Clear and redistribute
    editor.innerHTML = '';

    const I = imageGroups.length;

    // 소제목 판단 함수: H태그이거나 특정 특수문자(▶, ▮, ◆)로 시작하는 경우
    const isHeading = (el: Node) => {
      if (el.nodeType !== Node.ELEMENT_NODE) return false;
      const element = el as Element;
      const tagName = element.tagName.toUpperCase();
      if (/^H[1-6]$/.test(tagName)) return true;
      const text = element.textContent?.trim() || "";
      return /^[#\s\-*]*[▶▮◆]/.test(text);
    };

    const appendGroup = (group: { img: HTMLImageElement, caption?: Node }) => {
      // Create a container for image and caption to ensure they stay together
      const container = document.createElement('div');
      container.style.textAlign = 'center';
      container.style.margin = '20px 0';
      container.className = 'image-container';

      container.appendChild(group.img);
      if (group.caption) {
        // If it's a text node, wrap it in a div or p to style it
        if (group.caption.nodeType === Node.TEXT_NODE) {
          const capDiv = document.createElement('div');
          capDiv.style.fontSize = '0.9em';
          capDiv.style.color = '#666';
          capDiv.style.marginTop = '8px';
          capDiv.appendChild(group.caption);
          container.appendChild(capDiv);
        } else {
          // If it already is an element, just append it
          container.appendChild(group.caption);
        }
      }
      editor.appendChild(container);
    };

    // 연속된 소제목 + 다음 본문 문단을 하나의 단위로 묶어 이미지가 소제목 직후에 끼지 않도록 함
    const units: Node[][] = [];
    let buffer: Node[] = [];
    nonImageElements.forEach(node => {
      buffer.push(node);
      if (!isHeading(node)) {
        units.push(buffer);
        buffer = [];
      }
    });
    // 글 끝에 본문 없이 남은 소제목들은 별도로 보관해 이미지 슬롯 계산에서 제외하고 가장 뒤에 붙임
    const trailing: Node[] = buffer;

    const N = units.length;
    const slots = new Array(N + 1).fill(0);
    slots[0] = 1; // 글 맨 앞에는 반드시 1개

    if (N === 0) {
      slots[0] = I;
    } else if (I > 1) {
      const remaining = I - 1;
      const base = Math.floor(remaining / N);
      const extra = remaining % N;
      for (let i = 1; i <= N; i++) slots[i] = base;
      // 추가분은 가운데 쪽으로 균등하게 흩뿌림
      for (let k = 0; k < extra; k++) {
        const pos = Math.round(((k + 1) * N) / (extra + 1));
        const idx = Math.max(1, Math.min(N, pos));
        slots[idx] += 1;
      }
    }

    let imgIdx = 0;
    const place = (count: number) => {
      for (let i = 0; i < count && imgIdx < I; i++) {
        appendGroup(imageGroups[imgIdx++]);
      }
    };

    place(slots[0]);
    units.forEach((unit, i) => {
      unit.forEach(node => editor.appendChild(node));
      place(slots[i + 1]);
    });
    trailing.forEach(node => editor.appendChild(node));

    // 재배치 후에도 현재 출처 표시 설정 유지
    setSourceCaptionsVisibility(editor, showSourceCaptions);

    pushHistory();
    saveContent(true);
    showToast("이미지와 출처가 함께 배치되었습니다.");
  };

  const handleReplaceAll = () => {
    if (!findText) return;
    
    let count = 0;

    // 1. Replace in Title
    if (title.includes(findText)) {
      const newTitle = title.split(findText).join(replaceText);
      setTitle(newTitle);
      count += (title.split(findText).length - 1);
    }

    // 2. Replace in Body (DOM traversal to preserve tags)
    if (editorRef.current) {
      const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.nodeValue && node.nodeValue.includes(findText)) {
            const occurrences = node.nodeValue.split(findText).length - 1;
            count += occurrences;
            node.nodeValue = node.nodeValue.split(findText).join(replaceText);
          }
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            walk(node.childNodes[i]);
          }
        }
      };
      walk(editorRef.current);
    }

    if (count > 0) {
      pushHistory();
      saveContent(true);
      showToast(`${count}개의 단어를 변경했습니다.`);
    } else {
      showToast('찾는 단어가 없습니다.');
    }
  };

  const cleanSubtitles = () => {
    if (!editorRef.current) return;
    const SUBTITLE_SYMBOL_REGEX = /^(\s*)[▶◆▮▼●■★◇▪‣•]+\s*/u;
    const blocks = editorRef.current.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, div');
    let changed = false;
    blocks.forEach(block => {
      const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
      const firstTextNode = walker.nextNode() as Text | null;
      if (!firstTextNode || !firstTextNode.nodeValue) return;
      if (SUBTITLE_SYMBOL_REGEX.test(firstTextNode.nodeValue)) {
        firstTextNode.nodeValue = firstTextNode.nodeValue.replace(SUBTITLE_SYMBOL_REGEX, '$1');
        changed = true;
      }
    });
    if (changed) {
      pushHistory();
      saveContent(true);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault(); 
        const blob = items[i].getAsFile();
        if (!blob) continue;
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          if (base64) {
            execCmd('insertImage', base64);
          }
        };
        reader.readAsDataURL(blob);
      }
    }
  };

  const handleExportHtml = () => {
    if (!editorRef.current) return;
    const bodyHtml = editorRef.current.innerHTML;
    const titleLines = title.split('\n').filter(l => l.trim());
    const currentTask = tasks?.find(t => t.id === activeTaskId);
    
    if (!currentTask) {
      alert("문서 정보를 찾을 수 없습니다.");
      return;
    }

    const fullHtmlContent = generateFullHtml(currentTask, bodyHtml, titleLines[0]);

    const blob = new Blob([fullHtmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${titleLines[0] || 'document'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert('HTML 파일로 저장되었습니다.');
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = async (evt) => {
          const base64 = evt.target?.result as string;
          if (base64) {
              editorRef.current?.focus();
              execCmd('insertImage', base64);
              // Explicitly trigger save after image insertion
              await saveContent(true);
          }
        };
        reader.readAsDataURL(file);
      });
    }
    e.target.value = '';
  };

  const handleCopyBody = async () => {
    if (!editorRef.current) return;
    try {
        // 숨겨진 출처 캡션(display:none)은 복사 HTML에서 제외.
        // innerText는 display:none을 이미 제외하므로 텍스트는 라이브 에디터에서 가져온다.
        const clone = editorRef.current.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('.source-caption').forEach(el => {
          if ((el as HTMLElement).style.display === 'none') el.remove();
        });
        const contentHtml = clone.innerHTML;
        const contentText = editorRef.current.innerText;
        const blobHtml = new Blob([contentHtml], { type: 'text/html' });
        const blobText = new Blob([contentText], { type: 'text/plain' });
        
        // Fix: Cast to any to handle potential ClipboardItem type incompatibility or missing global definition in specific environments
        const ClipboardItemConstructor = (window as any).ClipboardItem;
        if (typeof ClipboardItemConstructor !== 'undefined') {
          // Fix: Explicitly cast blob arguments to any to resolve 'unknown' to 'Blob' conversion errors in strict environments
          const data = [new ClipboardItemConstructor({ 'text/html': blobHtml as any, 'text/plain': blobText as any })];
          await (navigator.clipboard as any).write(data);
          showToast('본문(제목 제외)이 복사되었습니다.');
        } else {
          // Fallback if ClipboardItem is not supported
          await navigator.clipboard.writeText(contentText);
          showToast('본문이 텍스트 형식으로 복사되었습니다 (HTML 복사 미지원 브라우저).');
        }
    } catch (err) {
        console.error('Copy failed:', err);
        showToast('복사 중 오류가 발생했습니다.');
    }
  };

  const handleLaunchEditor = async () => {
    if (selectedImgNodes.length === 0) return;
    const selectedImgNode = selectedImgNodes[0];
    const src = selectedImgNode.src;
    
    if (src.startsWith('data:image')) {
      const base64 = src.split(',')[1];
      setEditingImage({ base64, mimeType: 'image/png' });
      setShowImageEditor(true);
    } else {
      try {
        const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(src)}`;
        const response = await fetch(proxyUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          const res = reader.result as string;
          const base64 = res.split(',')[1];
          setEditingImage({ base64, mimeType: 'image/png' });
          setShowImageEditor(true);
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        alert("이미지를 편집용으로 불러올 수 없습니다. 이미지를 다운로드 후 직접 업로드하거나 복사/붙여넣기 해주세요.");
      }
    }
  };

  const handleImageEditorSave = async (newBase64: string) => {
    if (selectedImgNodes.length > 0) {
        const img = selectedImgNodes[0];
        img.src = `data:image/png;base64,${newBase64}`;
        img.removeAttribute('data-watermarked');
        pushHistory();
        saveContent(true);
    }
    setShowImageEditor(false);
    setEditingImage(null);
  };

  const getTaskTitle = (task: any) => {
    return task.presetName || 'Untitled';
  };

  return (
    <div className="flex h-full bg-paper text-slate-800 relative">
      {/* Toast */}
      <AnimatePresence>
        {toast.visible && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] bg-slate-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-bold pointer-events-none"
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs Sidebar */}
      {isSidebarOpen && (
      <div className="fixed inset-y-0 left-0 z-[110] w-84 md:relative md:inset-auto md:z-auto bg-slate-100 border-r border-slate-200 flex flex-col overflow-hidden flex-shrink-0">
        {/* Agents Section */}
        <div className="p-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 px-1">에이전트 필터</h3>
              <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                <button
                  onClick={() => {
                    isSwitchingRef.current = true;
                    onSelectAgent('all');
                  }}
                  className={`w-full text-left px-3 py-1 rounded text-xs font-bold transition-colors flex items-center gap-2 ${
                    selectedAgentName === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  전체 에이전트
                </button>
                {agents.map(agent => (
                  <button
                    key={agent.id}
                    onClick={() => {
                      isSwitchingRef.current = true;
                      onSelectAgent(agent.name);
                    }}
                    className={`w-full text-left px-3 py-1 rounded text-xs font-bold transition-colors truncate ${
                      selectedAgentName === agent.name ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {agent.name}
                  </button>
                ))}
              </div>
            </div>

          {/* Task List Section */}
          <div 
            ref={sidebarScrollRef}
            onScroll={handleSidebarScroll}
            className="flex-1 overflow-y-auto no-scrollbar"
          >
            {filteredTasks.map((task) => {
              const isModified = !!task.hasLocalEdits;
              return (
                <button
                  key={task.id}
                  onDoubleClick={(e) => handleStartRename(e, task)}
                  onClick={async () => {
                    if (task.id !== activeTaskId) {
                      isSwitchingRef.current = true;
                      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
                      if (isDirty) await saveContent(false);
                      onSwitchTask?.(task.id);
                    }
                  }}
                  className={`px-4 py-2 text-[11px] font-bold border-b border-slate-200 text-left transition-colors flex items-center gap-2 ${
                    task.id === activeTaskId 
                      ? 'bg-white text-primary border-r-4 border-r-primary' 
                      : task.isRegistered 
                        ? 'bg-sky-50 text-slate-600 hover:bg-sky-100'
                        : 'text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  <div className="flex flex-col gap-1 w-full">
                    {editingTaskId === task.id ? (
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={tempPresetName}
                        onChange={(e) => setTempPresetName(e.target.value)}
                        onBlur={handleFinishRename}
                        onKeyDown={handleRenameKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0.5 bg-white border border-blue-500 rounded outline-none text-slate-900"
                      />
                    ) : (
                      <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between w-full">
                        <span className={`line-clamp-2 leading-tight flex-1 text-[14px] font-bold ${isModified ? 'text-orange-500' : ''}`}>{getTaskTitle(task)}</span>
                        {task.isRegistered && (
                          <span className="text-[9px] bg-blue-600 text-white px-1 py-0.5 rounded font-bold ml-1 flex-shrink-0">등록 완료</span>
                        )}
                      </div>
                      <span className="text-[14px] text-slate-400 font-normal">
                        {new Date(task.createdAt).toLocaleDateString()} {new Date(task.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="bg-white border-b border-slate-200 p-2 md:p-3 flex flex-col md:flex-row justify-between items-start md:items-center gap-2 shadow-sm flex-shrink-0 z-10 sticky top-0">
        <div className="flex flex-wrap items-center gap-2 md:gap-4">
          <button onClick={handleBack} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded-lg transition-colors group text-slate-500 hover:text-slate-900" title="저장하고 뒤로 가기">
            <span className="font-bold">뒤로</span>
          </button>
          <div className="h-6 w-px bg-slate-200 mx-2"></div>
          <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200">
            <div className="relative">
              <button 
                onClick={() => setShowEditMenu(!showEditMenu)}
                className={`px-3 py-1.5 rounded hover:bg-slate-100 text-slate-700 font-bold transition-colors text-xs flex items-center gap-1 ${showEditMenu ? 'bg-slate-100' : ''}`}
                title="텍스트 서식 수정"
              >
                수정 <ChevronDown size={12} className={`transition-transform ${showEditMenu ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {showEditMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowEditMenu(false)}></div>
                    <motion.div 
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      className="absolute top-full left-0 mt-1 w-32 bg-white rounded-lg border border-slate-200 shadow-xl p-1 z-50"
                    >
                      <ToolbarButton onClick={() => { execCmd('bold'); setShowEditMenu(false); }} label="굵게" title="굵게 (Ctrl+B)" className="w-full text-left" />
                      <ToolbarButton onClick={() => { execCmd('italic'); setShowEditMenu(false); }} label="기울임" title="기울임 (Ctrl+I)" className="w-full text-left" />
                      <ToolbarButton onClick={() => { execCmd('formatBlock', 'H2'); setShowEditMenu(false); }} label="제목" title="제목 2" className="w-full text-left" />
                      <ToolbarButton onClick={() => { execCmd('formatBlock', 'PRE'); setShowEditMenu(false); }} label="코드" title="코드 블록" className="w-full text-left" />
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <div className="h-4 w-px bg-slate-200 mx-1"></div>
            <ToolbarButton onClick={() => execCmd('insertUnorderedList')} label="글머리" title="글머리 기호" />
            <ToolbarButton onClick={cleanSubtitles} label="부제목 정리" title="▶ ◆ ▮ 등 부제목 앞 기호 일괄 제거" />
            <ToolbarButton onClick={() => execCmd('insertOrderedList')} label="번호" title="번호 매기기" />
            <div className="h-4 w-px bg-slate-200 mx-1"></div>
            <ToolbarButton onClick={() => setShowAlbum(true)} label="앨범" title="이미지 앨범 보기" />
            <div className="h-4 w-px bg-slate-200 mx-1"></div>
            <label className="px-3 py-1.5 rounded hover:bg-slate-100 text-slate-700 font-bold cursor-pointer transition-colors text-xs" title="이미지 삽입">
                이미지
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
            </label>
            <button
              onClick={distributeImages}
              className="px-3 py-1.5 rounded font-bold transition-colors text-xs flex items-center gap-1.5 hover:bg-emerald-50 text-emerald-600"
              title="이미지 고르게 배치"
            >
              <LayoutGrid size={14} />
              이미지 배치
            </button>
            <ToolbarButton onClick={() => setShowFindReplace(!showFindReplace)} label="찾기/바꾸기" title="단어 찾기 및 바꾸기" />
             {selectedImgNodes.length > 0 && (
                <>
                  <div className="h-4 w-px bg-slate-200 mx-1"></div>
                  <button onClick={handleLaunchEditor} className="px-3 py-1.5 rounded hover:bg-blue-100 text-blue-600 font-bold transition-colors text-xs" title="이미지 편집">
                    편집
                  </button>
                  <button
                    onClick={() => {
                      selectedImgNodes.forEach(node => node.remove());
                      setSelectedImgNodes([]);
                      pushHistory();
                      saveContent(true);
                    }}
                    className="px-3 py-1.5 rounded hover:bg-red-100 text-red-600 font-bold transition-colors text-xs"
                    title="이미지 삭제"
                  >
                    삭제
                  </button>
                </>
            )}
          </div>
          <div className="hidden md:flex items-center gap-1 ml-2">
            <ToolbarButton onClick={handleUndo} label="취소" title="실행 취소" />
            <ToolbarButton onClick={handleRedo} label="다시" title="다시 실행" />
          </div>
        </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <a 
                href={activeTask?.notionPageId ? `https://www.notion.so/flowing/${activeTask.notionPageId.replace(/-/g, '')}` : "https://www.notion.so/flowing/337bb2c4092280ec999bee37a22a5ee8?v=337bb2c40922800292b2000c7f3b5677"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-sm font-bold text-slate-600 transition-colors border border-slate-200 flex items-center gap-2"
            >
                노션 확인
            </a>
            <div className="relative">
              <button
                onClick={async () => {
                  if (isDirty) await saveContent(false);
                  onToggleRegister?.(!isRegistered);
                }}
                className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all shadow-sm flex items-center gap-2 ${
                  isRegistered
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${isRegistered ? 'bg-white animate-pulse' : 'bg-slate-300'}`}></div>
                {isRegistered ? '등록 완료' : '등록'}
              </button>
              {showSettings && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowSettings(false)}
                  ></div>
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-2xl p-5 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <h3 className="text-xs font-bold text-slate-400 mb-4 uppercase tracking-widest">등록 설정</h3>
                    
                    <div className="mb-4">
                      <label className="block text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">카테고리</label>
                      <select 
                        value={effectiveCategory}
                        onChange={(e) => onToggleCategory?.(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 font-bold text-slate-700"
                      >
                        <option value="">선택 안함</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-6">
                      <label className="flex items-center gap-3 cursor-pointer select-none group p-2 rounded-lg hover:bg-slate-50 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={isWeekend} 
                          onChange={(e) => onToggleWeekend?.(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900 transition-colors">주말 발행</span>
                      </label>
                    </div>

                    <button 
                      onClick={async () => {
                        console.log(`[Editor] Register button clicked. Current isRegistered: ${isRegistered}`);
                        try {
                          console.log(`[Editor] Calling saveContent...`);
                          if (isDirty) await saveContent(false);
                          console.log(`[Editor] saveContent completed. Calling onToggleRegister...`);
                          onToggleRegister?.(!isRegistered);
                          console.log(`[Editor] onToggleRegister called.`);
                        } catch (err) {
                          console.error(`[Editor] Error in Register button click handler:`, err);
                        }
                        setShowSettings(false);
                      }}
                      className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        isRegistered 
                          ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' 
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'
                      }`}
                    >
                      {isRegistered ? (
                        <>
                          <CheckCircle2 size={16} />
                          <span>등록 취소</span>
                        </>
                      ) : (
                        <span>등록하기</span>
                      )}
                    </button>

                    {isRegistered && (
                      <div className="mt-6 pt-6 border-t border-slate-100">
                        <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-3">공개 뉴스 사이트</h4>
                        <div className="grid gap-2">
                          <a 
                            href={`/news/${activeTaskId}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors group"
                          >
                            <span className="text-xs font-bold text-blue-700">기사 보기</span>
                            <Settings size={12} className="text-blue-400 group-hover:rotate-90 transition-transform" />
                          </a>
                          <a 
                            href="/news" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-colors group"
                          >
                            <span className="text-xs font-bold text-slate-600">뉴스 포털 홈</span>
                            <Settings size={12} className="text-slate-400 group-hover:rotate-90 transition-transform" />
                          </a>
                        </div>
                        <p className="mt-3 text-[10px] text-slate-400 font-medium leading-relaxed">
                          * 검색 엔진에 노출되지 않도록 설정되어 있습니다.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="h-6 w-px bg-slate-200 mx-1"></div>
            <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200 mr-2">
              <button onClick={handleCopyBody} className="px-3 py-1.5 rounded hover:bg-slate-100 text-slate-700 font-bold transition-colors text-xs" title="본문 복사 (HTML/텍스트)">
                본문 복사
              </button>
            </div>
            <div className="flex items-center gap-1 text-xs font-medium mr-2">
                {lastSaveStatus === 'saving' && <span className="text-slate-400 flex items-center gap-1">자동 저장중...</span>}
                {lastSaveStatus === 'saved' && <span className="text-green-600 flex items-center gap-1">저장됨</span>}
            </div>
            <button onClick={handleExportHtml} className="flex items-center gap-2 px-4 py-1.5 bg-primary hover:bg-blue-600 rounded-md text-sm transition-colors text-white font-bold shadow-sm">
              HTML 저장
            </button>
          </div>
      </div>
      
      {showFindReplace && (
        <div className="bg-white border-b border-slate-200 p-2 flex flex-wrap items-center gap-2 md:gap-3 px-3 md:px-6 animate-in slide-in-from-top duration-200 shadow-sm z-[5]">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">찾기:</span>
            <input 
              type="text" 
              value={findText} 
              onChange={(e) => setFindText(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-300 rounded bg-white outline-none focus:border-primary w-40"
              placeholder="찾을 단어..."
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">바꾸기:</span>
            <input 
              type="text" 
              value={replaceText} 
              onChange={(e) => setReplaceText(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-300 rounded bg-white outline-none focus:border-primary w-40"
              placeholder="바꿀 단어..."
            />
          </div>
          <button 
            onClick={handleReplaceAll}
            className="px-3 py-1 bg-slate-900 text-white text-xs font-bold rounded hover:bg-slate-800 transition-colors"
          >
            모두 바꾸기
          </button>
          <button 
            onClick={() => setShowFindReplace(false)}
            className="text-xs font-bold text-slate-400 hover:text-slate-600 ml-auto"
          >
            닫기
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="max-w-5xl mx-auto p-6 md:p-10 flex flex-col gap-8 pb-24">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden shrink-0">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">카테고리 설정</span>
                </div>
                <div className="p-6 flex flex-col gap-4">
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <div 
                        key={cat}
                        className={`group flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer border ${
                          effectiveCategory === cat 
                            ? 'bg-blue-600 text-white border-blue-700 shadow-md' 
                            : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                        }`}
                        onClick={() => onToggleCategory?.(cat)}
                      >
                        <span>{cat}</span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveCategory(cat);
                          }}
                          className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity ml-1"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <button 
                      onClick={() => setShowAddCategory(true)}
                      className="px-3 py-1.5 rounded-full text-sm font-bold bg-white border border-dashed border-slate-300 text-slate-400 hover:border-blue-500 hover:text-blue-500 transition-all"
                    >
                      + 추가
                    </button>
                  </div>

                  <AnimatePresence>
                    {showAddCategory && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex items-center gap-2 overflow-hidden"
                      >
                        <input 
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="새 카테고리 이름..."
                          className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500"
                          onKeyDown={(e) => e.key === 'Enter' && onAddCategory()}
                        />
                        <button 
                          onClick={onAddCategory}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          추가
                        </button>
                        <button 
                          onClick={() => setShowAddCategory(false)}
                          className="p-2 text-slate-400 hover:text-slate-600"
                        >
                          <X size={20} />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden shrink-0">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">기사 제목 (Title)</span>
                </div>
                <div className="p-6 md:p-8">
                    <textarea ref={titleInputRef} value={title} onChange={handleTitleChange} placeholder="기사 제목..." rows={1} className="w-full text-[1.1rem] font-bold text-slate-900 bg-transparent outline-none placeholder:text-slate-300 resize-none overflow-hidden leading-relaxed" style={{ minHeight: '40px' }} />
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden shrink-0">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">제목 추천 (Recommended Titles)</span>
                </div>
                <div className="p-6 md:p-8 space-y-4">
                    <div
                        ref={recTitlesEditorRef}
                        contentEditable
                        onInput={handleRecTitlesInput}
                        className="markdown-body outline-none"
                        suppressContentEditableWarning={true}
                        style={{ minHeight: '60px' }}
                    />
                    
                    {/* Source Links / Grounding URLs */}
                    {(() => {
                      const urlRegex = /https?:\/\/[^\s()<>"]+/g;
                      const parsedUrls = recommendedTitles.match(urlRegex) || [];
                      const hasGrounding = activeTask?.groundingUrls && activeTask.groundingUrls.length > 0;
                      
                      if (!hasGrounding && parsedUrls.length === 0) return null;

                      return (
                        <div className="pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">출처 및 참고 링크 (Source Links)</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {/* Render groundingUrls from task if they exist */}
                            {activeTask?.groundingUrls?.map((link, idx) => (
                              <a 
                                key={`grounding-${idx}`}
                                href={link.uri} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold hover:bg-blue-100 transition-colors border border-blue-100 max-w-sm"
                              >
                                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.101-1.101" />
                                </svg>
                                <span className="truncate">{link.title || '출처 링크'}</span>
                              </a>
                            ))}
                            
                            {/* Also parse URLs from the text if any weren't in groundingUrls */}
                            {parsedUrls.filter(url => 
                              !activeTask?.groundingUrls?.some(g => g.uri === url)
                            ).map((url, idx) => (
                              <a 
                                key={`parsed-${idx}`}
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 text-slate-600 rounded-full text-xs font-semibold hover:bg-slate-100 transition-colors border border-slate-200 max-w-sm"
                              >
                                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                <span className="truncate">{url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[400px]">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">메타 디스크립션 / 본문 (Content)</span>
                </div>
                <div className="p-6 md:p-12 flex-1 flex flex-col">
                    <div 
                      ref={editorRef} 
                      contentEditable 
                      onPaste={handlePaste} 
                      onInput={debouncedSave}
                      className="markdown-body outline-none flex-1" 
                      suppressContentEditableWarning={true} 
                    />
                </div>
            </div>
        </div>
      </div>
      
      {showImageEditor && editingImage && (
        <ImageEditor 
            image={editingImage} 
            onClose={() => { setShowImageEditor(false); setEditingImage(null); }} 
            onSave={handleImageEditorSave} 
        />
      )}

      {/* Album Modal */}
      <AnimatePresence>
        {showAlbum && (
          <AlbumModal
            editorRef={editorRef}
            onClose={() => setShowAlbum(false)}
            onDelete={() => { pushHistory(); saveContent(true); }}
            onApplyWatermark={() => applyWatermarksToEditor(true)}
            onDistribute={distributeImages}
            showCaptions={showSourceCaptions}
            onToggleCaptions={setShowSourceCaptions}
          />
        )}
      </AnimatePresence>
    </div>
    </div>
  );
};

interface AlbumModalProps {
  editorRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onDelete: () => void;
  onApplyWatermark: () => Promise<void>;
  onDistribute: () => void;
  showCaptions: boolean;
  onToggleCaptions: (visible: boolean) => void;
}

const AlbumModal: React.FC<AlbumModalProps> = ({ editorRef, onClose, onDelete, onApplyWatermark, onDistribute, showCaptions, onToggleCaptions }) => {
  const [images, setImages] = useState<{ src: string; node: HTMLImageElement; caption: string }[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isWatermarking, setIsWatermarking] = useState(false);
  const [selectionBox, setSelectionBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const lastSelectedIndexRef = useRef<number | null>(null);

  const refreshImages = useCallback(() => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    const imgNodes = Array.from(editor.querySelectorAll('img'));
    setImages(imgNodes.map(node => {
      const cap = findCaptionNode(node, editor);
      return { src: node.src, node, caption: cap?.textContent?.trim() ?? '' };
    }));
  }, [editorRef]);

  useEffect(() => { refreshImages(); }, [refreshImages]);

  // "출처 표시" 체크박스 토글: 본문 캡션 표시/숨김 후 저장.
  const handleToggleCaptions = (visible: boolean) => {
    onToggleCaptions(visible);
    if (editorRef.current) setSourceCaptionsVisibility(editorRef.current, visible);
    onDelete();
  };

  // 이미지 배치: 본문에 이미지를 고르게 재배치한 뒤 앨범 목록 갱신.
  const handleDistribute = () => {
    onDistribute();
    setSelectedIndices(new Set());
    lastSelectedIndexRef.current = null;
    refreshImages();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;
    
    // If clicking on an image item, don't start marquee
    if ((e.target as HTMLElement).closest('.album-item')) return;

    if (!isMultiSelect) {
      setSelectedIndices(new Set());
    }

    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX = e.clientX - rect.left + (gridRef.current?.scrollLeft || 0);
    const startY = e.clientY - rect.top + (gridRef.current?.scrollTop || 0);

    setSelectionBox({ x1: startX, y1: startY, x2: startX, y2: startY });

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const rect = gridRef.current?.getBoundingClientRect();
      if (!rect) return;

      const currentX = moveEvent.clientX - rect.left + (gridRef.current?.scrollLeft || 0);
      const currentY = moveEvent.clientY - rect.top + (gridRef.current?.scrollTop || 0);
      
      setSelectionBox(prev => prev ? { ...prev, x2: currentX, y2: currentY } : null);

      // Auto-scroll logic
      const scrollThreshold = 80;
      const scrollSpeed = 15;
      if (moveEvent.clientY < rect.top + scrollThreshold) {
        gridRef.current?.scrollBy({ top: -scrollSpeed });
      } else if (moveEvent.clientY > rect.bottom - scrollThreshold) {
        gridRef.current?.scrollBy({ top: scrollSpeed });
      }

      // Calculate which items are in the box
      const box = {
        left: Math.min(startX, currentX),
        top: Math.min(startY, currentY),
        right: Math.max(startX, currentX),
        bottom: Math.max(startY, currentY)
      };

      const newSelection = isMultiSelect ? new Set(selectedIndices) : new Set<number>();
      const items = gridRef.current?.querySelectorAll('.album-item');
      items?.forEach((item, index) => {
        const itemEl = item as HTMLElement;
        const itemRect = {
          left: itemEl.offsetLeft,
          top: itemEl.offsetTop,
          right: itemEl.offsetLeft + itemEl.offsetWidth,
          bottom: itemEl.offsetTop + itemEl.offsetHeight
        };

        const isIntersecting = !(
          itemRect.right < box.left ||
          itemRect.left > box.right ||
          itemRect.bottom < box.top ||
          itemRect.top > box.bottom
        );

        if (isIntersecting) {
          newSelection.add(index);
        } else if (!isMultiSelect) {
          newSelection.delete(index);
        }
      });
      setSelectedIndices(newSelection);
    };

    const handleMouseUp = () => {
      setSelectionBox(null);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleItemClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const isCtrl = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;

    if (isShift && lastSelectedIndexRef.current !== null) {
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);
      const newSelection = new Set(selectedIndices);
      for (let i = start; i <= end; i++) {
        newSelection.add(i);
      }
      setSelectedIndices(newSelection);
    } else if (isCtrl) {
      const newSelection = new Set(selectedIndices);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      setSelectedIndices(newSelection);
      lastSelectedIndexRef.current = index;
    } else {
      setSelectedIndices(new Set([index]));
      lastSelectedIndexRef.current = index;
    }
  };

  const handleDelete = useCallback(() => {
    if (selectedIndices.size === 0) return;
    
    const indicesToDelete = Array.from(selectedIndices).sort((a, b) => b - a);
    indicesToDelete.forEach(index => {
      const imgInfo = images[index];
      if (imgInfo && imgInfo.node) {
        const node = imgInfo.node;
        const caption = findCaptionNode(node, editorRef.current as HTMLElement);
        if (caption) {
          caption.parentNode?.removeChild(caption);
        }
        node.remove();
      }
    });

    // Update local state to reflect deletion without closing
    setImages(prev => prev.filter((_, i) => !selectedIndices.has(i)));
    setSelectedIndices(new Set());
    lastSelectedIndexRef.current = null;

    onDelete();
  }, [selectedIndices, images, onDelete]);

  const handleDeleteAll = useCallback(() => {
    if (images.length === 0) return;

    images.forEach(imgInfo => {
      if (imgInfo.node) {
        const node = imgInfo.node;
        const caption = findCaptionNode(node, editorRef.current as HTMLElement);
        if (caption) {
          caption.parentNode?.removeChild(caption);
        }
        node.remove();
      }
    });

    setImages([]);
    setSelectedIndices(new Set());
    lastSelectedIndexRef.current = null;
    onDelete();
  }, [images, editorRef, onDelete]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl + A or Cmd + A (Select All)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        const active = document.activeElement;
        const isInput = active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA' || (active as HTMLElement)?.isContentEditable;
        
        if (!isInput && images.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndices(new Set(images.map((_, i) => i)));
        }
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const active = document.activeElement;
        const isInput = active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA' || (active as HTMLElement)?.isContentEditable;
        
        if (!isInput && selectedIndices.size > 0) {
          e.preventDefault();
          e.stopPropagation();
          handleDelete();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleDelete, selectedIndices, images]);

  const handleWatermark = async () => {
    setIsWatermarking(true);
    try {
      await onApplyWatermark();
      // Refresh album images
      if (editorRef.current) {
        const editor = editorRef.current;
        const imgNodes = Array.from(editor.querySelectorAll('img'));
        setImages(imgNodes.map(node => {
          const cap = findCaptionNode(node, editor);
          return { src: node.src, node, caption: cap?.textContent?.trim() ?? '' };
        }));
      }
    } finally {
      setIsWatermarking(false);
    }
  };

  const handleShuffle = useCallback(() => {
    if (images.length < 2) return;

    const editor = editorRef.current;
    if (!editor) return;

    // Fisher-Yates shuffle
    const shuffled = [...images];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Collect and remove existing image groups from DOM
    const allImgNodes = Array.from(editor.querySelectorAll('img'));
    const groups: { container: HTMLElement | null; img: HTMLImageElement; caption: Node | undefined }[] = [];

    allImgNodes.forEach(img => {
      const caption = findCaptionNode(img, editor);
      const container = img.closest('.image-container') as HTMLElement | null;
      groups.push({ container, img, caption });
    });

    groups.forEach(g => {
      if (g.container && g.container.parentNode) {
        g.container.parentNode.removeChild(g.container);
      } else {
        if (g.caption && g.caption.parentNode) {
          g.caption.parentNode.removeChild(g.caption);
        }
        if (g.img.parentNode) {
          g.img.parentNode.removeChild(g.img);
        }
      }
    });

    // Re-append in shuffled order
    shuffled.forEach(imgInfo => {
      const group = groups.find(g => g.img === imgInfo.node);
      if (!group) return;

      if (group.container) {
        editor.appendChild(group.container);
      } else {
        const wrapper = document.createElement('div');
        wrapper.className = 'image-container';
        wrapper.style.textAlign = 'center';
        wrapper.style.margin = '20px 0';
        wrapper.appendChild(group.img);
        if (group.caption) {
          if (group.caption.nodeType === Node.TEXT_NODE) {
            const capDiv = document.createElement('div');
            capDiv.style.fontSize = '0.9em';
            capDiv.style.color = '#666';
            capDiv.style.marginTop = '8px';
            capDiv.appendChild(group.caption);
            wrapper.appendChild(capDiv);
          } else {
            wrapper.appendChild(group.caption);
          }
        }
        editor.appendChild(wrapper);
      }
    });

    setImages(shuffled);
    setSelectedIndices(new Set());
    lastSelectedIndexRef.current = null;
    onDelete();
  }, [images, editorRef, onDelete]);

  const handleSetFirst = useCallback((index: number) => {
    const editor = editorRef.current;
    if (!editor || index === 0 || !images[index]) return;

    const target = images[index];
    const container = target.node.closest('.image-container') as HTMLElement | null;
    const caption = findCaptionNode(target.node, editor);

    // DOM: 대상 그룹을 에디터 맨 앞으로 이동
    if (container) {
      editor.insertBefore(container, editor.firstChild);
    } else {
      editor.insertBefore(target.node, editor.firstChild);
      if (caption) editor.insertBefore(caption, target.node.nextSibling);
    }

    // state 재정렬: 대상을 배열 맨 앞으로
    setImages(prev => {
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.unshift(moved);
      return next;
    });
    setSelectedIndices(new Set());
    lastSelectedIndexRef.current = null;
    onDelete();
  }, [images, editorRef, onDelete]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center"
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full h-full shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
              <ImageIcon size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">이미지 앨범</h2>
              <p className="text-xs text-slate-500 font-medium">{images.length}개의 이미지 / {selectedIndices.size}개 선택됨</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 px-2 text-sm font-medium text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showCaptions}
                onChange={(e) => handleToggleCaptions(e.target.checked)}
                className="w-4 h-4 accent-purple-600"
              />
              <span>출처 표시</span>
            </label>
            <button
              onClick={handleShuffle}
              disabled={images.length < 2}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-bold shadow-sm ${
                images.length < 2
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <Shuffle size={16} />
              <span>섞기</span>
            </button>
            <button
              onClick={handleWatermark}
              disabled={isWatermarking}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-bold shadow-sm ${
                isWatermarking 
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isWatermarking ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>처리 중...</span>
                </>
              ) : (
                <>
                  <ImageIcon size={16} />
                  <span>워터마크 일괄 적용</span>
                </>
              )}
            </button>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            <button 
              onClick={handleDeleteAll}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors text-sm font-bold"
            >
              <Trash2 size={16} />
              <span>전체 삭제</span>
            </button>
            {selectedIndices.size > 0 && (
              <button 
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors text-sm font-bold"
              >
                <Trash2 size={16} />
                <span>선택 삭제</span>
              </button>
            )}
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            <button
              onClick={handleDistribute}
              disabled={images.length === 0}
              title="이미지 고르게 배치"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-bold shadow-sm ${
                images.length === 0
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              <LayoutGrid size={16} />
              <span>이미지 배치</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div 
          ref={gridRef}
          className="flex-1 overflow-y-auto p-[60px] relative select-none custom-scrollbar"
          onMouseDown={handleMouseDown}
        >
          <div className="columns-2 md:columns-3 lg:columns-4 gap-2">
            {images.map((img, index) => (
              <div
                key={index}
                onClick={(e) => handleItemClick(e, index)}
                className={`album-item relative group break-inside-avoid mb-6 rounded-xl overflow-hidden border-4 transition-all cursor-pointer ${
                  selectedIndices.has(index)
                    ? 'border-blue-500 ring-4 ring-blue-100'
                    : 'border-transparent hover:border-slate-200'
                }`}
              >
                <div
                  className="relative"
                  onDoubleClick={(e) => { e.stopPropagation(); setLightboxSrc(img.src); }}
                >
                  <img
                    src={img.src}
                    alt={`Album ${index}`}
                    className="w-full h-auto block"
                    referrerPolicy="no-referrer"
                  />
                  <div className={`absolute inset-0 transition-colors ${selectedIndices.has(index) ? 'bg-blue-500/10' : 'group-hover:bg-black/5'}`}></div>
                  {index !== 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSetFirst(index); }}
                      title="이 이미지를 맨 앞으로 이동"
                      className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 text-white text-[11px] font-bold opacity-0 group-hover:opacity-100 transition-opacity hover:bg-purple-600"
                    >
                      <ArrowUpToLine size={12} /> 첫번째로
                    </button>
                  )}
                  {selectedIndices.has(index) && (
                    <div className="absolute top-3 right-3 bg-blue-500 text-white p-1 rounded-full shadow-lg">
                      <CheckCircle2 size={16} />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[10px] text-white font-bold">Image {index + 1}</span>
                  </div>
                </div>
                {showCaptions && img.caption && (
                  <div className="px-2 py-1.5 text-xs text-slate-600 bg-white border-t border-slate-100 leading-snug whitespace-pre-wrap break-words">
                    {img.caption}
                  </div>
                )}
              </div>
            ))}
          </div>

          {selectionBox && (
            <div 
              className="absolute border border-blue-500 bg-blue-500/20 pointer-events-none z-50"
              style={{
                left: Math.min(selectionBox.x1, selectionBox.x2),
                top: Math.min(selectionBox.y1, selectionBox.y2),
                width: Math.abs(selectionBox.x2 - selectionBox.x1),
                height: Math.abs(selectionBox.y2 - selectionBox.y1)
              }}
            />
          )}
        </div>

        {lightboxSrc && (
          <div
            className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-8 cursor-zoom-out"
            onClick={() => setLightboxSrc(null)}
          >
            <img
              src={lightboxSrc}
              referrerPolicy="no-referrer"
              className="max-w-full max-h-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightboxSrc(null)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
            >
              <X size={28} />
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const ToolbarButton: React.FC<{ onClick: () => void; label: string; title: string; className?: string }> = ({ onClick, label, title, className }) => (
    <button onClick={onClick} className={`px-3 py-1.5 rounded hover:bg-slate-100 text-slate-700 font-bold transition-colors text-xs ${className}`} title={title}>
        {label}
    </button>
);
